import React, { useCallback, useEffect, useState, useRef } from "react";

// core components
import store from "store";
import config from "config";
import { withRouter } from "react-router";
import { pickNativeIosPhoto } from "../../nativePhotoPicker";
import { isNativeIosPlatform } from "../../deviceInfo";
import GridContainer from "common/components/Grid/GridContainer.jsx";
import GridItem from "common/components/Grid/GridItem.jsx";

import Card from "common/components/Card/Card.jsx";
import CardHeader from "common/components/Card/CardHeader.jsx";
import CardIcon from "common/components/Card/CardIcon.jsx";
import CardBody from "common/components/Card/CardFooter.jsx";
import LoadingBox from "common/components/LoadingBox.js";
//

import Button from "common/components/CustomButtons/Button.jsx";
import Payment from "@material-ui/icons/Payment";

import dashboardStyle from "assets/jss/material-dashboard-pro-react/views/dashboardStyle";
import withStyles from "@material-ui/core/styles/withStyles";

const importedStyle = dashboardStyle;
const styles = {
  cardIconTitle: importedStyle.cardIconTitle,
  text: {
    margin: 20,
    textAlign: "center",
    alignSelf: "center",
  },
  buttonStyle: {
    margin: "20px",
  },
  highlight: {
    color: "green",
  },
  fileInput: {
    display: "none",
  },
};

const MAX_UPLOAD_SIZE = 10000000;
const JPEG_CONVERSION_QUALITY = 0.85;

const AddEFTPage = (props) => {
  const [buttonDisable, setButtonDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [nativeFile, setNativeFile] = useState(null);

  const fileRef = useRef(null);

  const { classes } = props;
  const useNativeIosUpload = isNativeIosPlatform();

  function getSelectedFile() {
    if (nativeFile != null) {
      return nativeFile;
    }

    if (
      fileRef == null ||
      fileRef.current == null ||
      !("files" in fileRef.current) ||
      fileRef.current.files.length < 1
    ) {
      return null;
    }

    return fileRef.current.files[0];
  }

  function isJpegFile(file) {
    const fileType = (file.type || "").toLowerCase();
    const selectedFileName = (file.name || "").toLowerCase();

    return (
      fileType === "image/jpeg" ||
      fileType === "image/jpg" ||
      selectedFileName.endsWith(".jpg") ||
      selectedFileName.endsWith(".jpeg")
    );
  }

  function isPngFile(file) {
    const fileType = (file.type || "").toLowerCase();
    const selectedFileName = (file.name || "").toLowerCase();

    return fileType === "image/png" || selectedFileName.endsWith(".png");
  }

  function convertImageToJpeg(file) {
    return new Promise((resolve, reject) => {
      const imageUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext("2d");

        if (context == null) {
          URL.revokeObjectURL(imageUrl);
          reject(new Error("Could not prepare image for upload."));
          return;
        }

        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0);
        URL.revokeObjectURL(imageUrl);
        canvas.toBlob(
          (jpegBlob) => {
            if (jpegBlob == null) {
              reject(new Error("Could not convert image to JPEG."));
              return;
            }

            const selectedFileName = file.name || "void-cheque.png";
            const jpegFileName = selectedFileName.replace(/\.png$/i, ".jpg");
            resolve(new File([jpegBlob], jpegFileName, { type: "image/jpeg" }));
          },
          "image/jpeg",
          JPEG_CONVERSION_QUALITY
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error("Could not read image for upload."));
      };
      image.src = imageUrl;
    });
  }

  async function normalizeUploadFile(file) {
    if (isJpegFile(file)) {
      return file;
    }

    if (isPngFile(file)) {
      return convertImageToJpeg(file);
    }

    return null;
  }

  const onInputChange = useCallback(() => {
    if (useNativeIosUpload) {
      setNativeFile(null);
    }
    const file =
      fileRef.current != null && fileRef.current.files.length > 0
        ? fileRef.current.files[0]
        : null;
    if (file == null) {
      setFileName(null);
      return;
    }

    if (useNativeIosUpload) {
      setErrorMessage("");
    }
    setFileName(file.name);
  }, [useNativeIosUpload]);

  useEffect(() => {
    if (useNativeIosUpload) {
      return undefined;
    }

    const fileInput = fileRef.current;
    fileInput.addEventListener("change", onInputChange);
    return () => fileInput.removeEventListener("change", onInputChange);
  }, [onInputChange, useNativeIosUpload]);

  async function handleSubmit(event) {
    if (useNativeIosUpload && event != null) {
      event.preventDefault();
    }

    setButtonDisabled(true);
    setLoading(true);

    if (!useNativeIosUpload) {
      if (fileRef.current.files.length < 1) {
        setButtonDisabled(false);
        setLoading(false);
        setErrorMessage("Please select a file");
        return;
      }

      const typeRegex = /(jpeg|jpg)/;
      const fileType = fileRef.current.files[0].type;
      const fileSize = fileRef.current.files[0].size;

      if (typeRegex.test(fileType) === false) {
        setButtonDisabled(false);
        setLoading(false);
        setErrorMessage("Wrong file type. Only jpeg/jpg accepted");
        return;
      }

      if (fileSize > 10000000) {
        setButtonDisabled(false);
        setLoading(false);
        setErrorMessage("File too big. Maximum size 5MB");
        console.log(fileSize);
        return;
      }

      if (fileSize < 50) {
        setButtonDisabled(false);
        setErrorMessage("Invalid file");
        setLoading(false);
        return;
      }

      try {
        const storeState = store.getState();
        const endPoint = `user.${storeState.services.ids.user}/eftphoto`;
        const url = `${config.API.ADDRESS}?target=${endPoint}&docpost=t`;
        const formData = new FormData();
        formData.append("file", fileRef.current.files[0]);
        const response = await fetch(url, {
          method: "post",
          headers: {
            "x-omicron": storeState.services.api.JRR,
          },
          body: formData,
        });
        const responseData = await response.json();
        console.log(response.status);
        console.log(responseData);
      } catch (error) {
        throw error;
      } finally {
        setErrorMessage("");
        setButtonDisabled(false);
        setLoading(false);
        props.backToMainScreen(
          "The photo you submited will be processed by our staff. You can go back to the checklist"
        );
      }
      return;
    }

    const file = getSelectedFile();
    if (file == null) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage("Please select a file");
      return;
    }

    if (file.size > MAX_UPLOAD_SIZE) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage("File too big. Maximum size 10MB");
      return;
    }

    let uploadFile;
    try {
      uploadFile = await normalizeUploadFile(file);
    } catch (e) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage(e.message || "Upload failed. Please try again.");
      return;
    }

    if (uploadFile == null) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage("Wrong file type. Choose a JPEG or PNG image.");
      return;
    }

    if (uploadFile.size > MAX_UPLOAD_SIZE) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage(
        "Converted JPEG is too big. Maximum upload size is 10MB."
      );
      return;
    }

    if (uploadFile.size < 50) {
      setButtonDisabled(false);
      setErrorMessage("Invalid file");
      setLoading(false);
      return;
    }

    try {
      const storeState = store.getState();
      const endPoint = `user.${storeState.services.ids.user}/eftphoto`;

      //URL built from config file and specified end point (target)
      let url = `${config.API.ADDRESS}?target=${endPoint}&docpost=t`;

      const formData = new FormData();
      formData.append("file", uploadFile);

      //myInit is the second fetch() argument used to specify methods, headers, body...
      let myInit: init = {
        method: "post",
        headers: {
          "x-omicron": storeState.services.api.JRR,
        },
        body: formData,
      };

      var res = await fetch(url, myInit);
      var resjson = await res.json();
      if (!res.ok || resjson.error) {
        throw new Error(resjson.message || "Upload failed");
      }

      setErrorMessage("");
      props.backToMainScreen(
        "The photo you submited will be processed by our staff. You can go back to the checklist"
      );
    } catch (e) {
      console.error(e);
      setErrorMessage(e.message || "Upload failed. Please try again.");
    } finally {
      setButtonDisabled(false);
      setLoading(false);
    }
  }

  async function clickOnChoosePhoto(event) {
    if (useNativeIosUpload && event != null) {
      event.preventDefault();
    }

    if (useNativeIosUpload) {
      try {
        const file = await pickNativeIosPhoto();
        if (file != null) {
          setNativeFile(file);
          setFileName(file.name);
          setErrorMessage("");
        }
      } catch (e) {
        setErrorMessage(e.message || "Could not select a photo.");
      }
      return;
    }

    if (fileRef.current != null) {
      fileRef.current.click();
    }
  }

  if (loading) {
    return <LoadingBox />;
  }

  return (
    <div>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <Payment />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>EFT</h4>
            </CardHeader>
            <CardBody>
              <GridContainer align="center" justify="center">
                <GridItem xs={12}>
                  <br />
                  <br />
                  <p>
                    {useNativeIosUpload
                      ? "Upload bank information photo i.e. void cheque (JPEG or PNG accepted; PNG photos will be converted to JPEG, max 10MB)"
                      : "Upload bank information photo i.e. void cheque (only jpeg/jpg accepted, max 10MB)"}
                  </p>
                  <font color="red">{errorMessage}</font>
                  <form>
                    <br />
                    <br />
                    <Button
                      className={classes.buttonStyle}
                      color={fileName == null ? "warning" : "success"}
                      onClick={clickOnChoosePhoto}
                    >
                      Choose Photo
                    </Button>
                    {fileName == null ? null : <p>{fileName}</p>}
                    <input
                      className={classes.fileInput}
                      type="file"
                      capture={useNativeIosUpload ? undefined : "environment"}
                      id="file1"
                      name="file1"
                      accept={
                        useNativeIosUpload
                          ? "image/jpeg,image/png,.jpg,.jpeg,.png"
                          : ".jpg, .jpeg"
                      }
                      onChange={useNativeIosUpload ? onInputChange : undefined}
                      ref={fileRef}
                    />
                    <br />
                    <br />
                    <br />
                  </form>
                </GridItem>
                <GridItem xs={12}>
                  <Button
                    className={classes.buttonStyle}
                    color="default"
                    disabled={buttonDisable}
                    onClick={props.backToMainScreen}
                  >
                    Back
                  </Button>
                  <Button
                    className={classes.buttonStyle}
                    color="primary"
                    disabled={buttonDisable}
                    onClick={handleSubmit}
                  >
                    Submit
                  </Button>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default withStyles(styles)(withRouter(AddEFTPage));
